/**
 * The pure model that turns a `/round/get-board` response, the contest
 * config, and a team name map into the view model the pick screen renders.
 * No React, no hooks, no clock read. Mirrors
 * `apps/app/src/features/poolRevive/reviveSheetModel.ts`.
 *
 * R4 compliance: availability is derived only from `board.selectableTeamIds`,
 * `board.lockedOutTeams` and `game.locked`. This module never reads a clock.
 */
import type {
  useContestGet,
  useRoundGetBoard,
} from "@sportsmet/contest-sdk/private";

import {
  PICK_ALREADY_FINISHED_TILE_LABEL,
  PICK_CONFIRM_MANY_LABEL,
  PICK_CONFIRM_ONE_LABEL,
  PICK_ELIMINATED_NOTE,
  PICK_FAILED_MESSAGE,
  PICK_LOCKED_TILE_LABEL,
  PICK_NEXT_CONTEST_NOTE,
  PICK_NO_CONTEST_NOTE,
  PICK_NO_ENTRY_NOTE,
  PICK_REUSE_LIMIT_LABEL,
  PICK_ROUND_LOCKED_NOTE,
  PICK_SKIPPED_NOTE,
  PICK_UPDATE_MANY_LABEL,
  PICK_UPDATE_ONE_LABEL,
  PICK_USED_THIS_WINDOW_LABEL,
} from "./pickCopy";

export type typeBoardData = NonNullable<
  NonNullable<ReturnType<typeof useRoundGetBoard>["data"]>["data"]
>;
export type typeContestData = NonNullable<
  NonNullable<ReturnType<typeof useContestGet>["data"]>["data"]
>;
export type typeContestStatus = typeContestData["status"];

// ISSUE-864. A contest ends when the Commissioner confirms it, so a contest in
// pendingOutcome is still being played and its locked round must stay on the
// fan's Picks tab for the whole confirm window. Reading only "active" here sent
// every such fan to the "no round to pick in" note and hid the picks they had
// already made. Mirrors isRunning in
// packages/contest-engine/src/picks/resolveViewerContest.ts and the standings
// model's CONTEST_SELECTION_STATUS_ORDER, which both already count
// pendingOutcome as live.
//
// Exported so a test can pin the membership itself. Asserting the list is the
// only way to catch a status being ADDED to it, which no per-status case can
// see, and it does that without a test having to name a status this product
// spells the British way.
export const PICK_PLAYABLE_CONTEST_STATUSES: readonly typeContestStatus[] = [
  "active",
  "pendingOutcome",
];
export type typeEntryStatus = "in" | "eliminated" | "revived";

export type typeSideAvailability =
  | { kind: "selectable" }
  | { kind: "selected"; order: number }
  | { kind: "locked"; label: string }
  | {
      kind: "lockedOut";
      reason: "usedThisWindow" | "reuseLimitReached";
      label: string;
    };

export type typePickTileSide = {
  teamId: string;
  teamName: string;
  teamAbbreviation: string;
  spreadLabel: string | null;
  isSpreadPending: boolean;
  availability: typeSideAvailability;
};

export type typePickTile = {
  eventId: string;
  kickoffAt: string;
  locked: boolean;
  unavailableReason: "alreadyFinished" | null;
  liveScore: { home: number; away: number } | null;
  home: typePickTileSide;
  away: typePickTileSide;
};

export type typeViewerContestState =
  "playing" | "waiting" | "spectating" | "none";

export type typePickBoardPhase =
  | { kind: "noContest"; note: string }
  | { kind: "waiting"; note: string }
  | { kind: "noEntry"; note: string }
  | { kind: "boardUnavailable"; note: string }
  | { kind: "eliminated"; note: string }
  | { kind: "skipped"; note: string }
  | { kind: "roundLocked"; note: string }
  | { kind: "confirmedReadOnly"; note: string }
  // ISSUE-1331. An earlier contest of the season, opened from the Game tab's
  // contest menu. Its board and picks show, and nothing can be changed.
  | { kind: "lookBack"; note: string }
  | { kind: "open"; requiredPickCount: number; canEdit: boolean };

export function formatSpreadLabel(input: {
  homeLine: number | null | undefined;
  isHome: boolean;
}): string | null {
  if (input.homeLine === null || typeof input.homeLine === "undefined") {
    return null;
  }
  const line = input.isHome ? input.homeLine : -input.homeLine;
  if (line === 0) return "PK";
  const rounded = Number(line.toFixed(1));
  const sign = rounded > 0 ? "+" : "-";
  return `${sign}${Math.abs(rounded)}`;
}

/** The event and team pair a tile's selection state is resolved on. ISSUE-767. */
export type typeSelectedSide = { eventId: string; teamId: string };

export function resolveSideAvailability(input: {
  eventId: string;
  teamId: string;
  gameLocked: boolean;
  unavailableReason: "alreadyFinished" | null;
  selections: readonly typeSelectedSide[];
  selectableTeamIds: readonly string[];
  lockedOutTeams: typeBoardData["lockedOutTeams"];
}): typeSideAvailability {
  if (input.gameLocked) {
    return {
      kind: "locked",
      label:
        input.unavailableReason === "alreadyFinished"
          ? PICK_ALREADY_FINISHED_TILE_LABEL
          : PICK_LOCKED_TILE_LABEL,
    };
  }
  const selectedIndex = input.selections.findIndex(
    (selection) =>
      selection.eventId === input.eventId && selection.teamId === input.teamId,
  );
  if (selectedIndex !== -1) {
    return { kind: "selected", order: selectedIndex + 1 };
  }
  const lockedOutUsed = input.lockedOutTeams.find(
    (team) => team.teamId === input.teamId && team.reason === "usedThisWindow",
  );
  if (lockedOutUsed) {
    return {
      kind: "lockedOut",
      reason: "usedThisWindow",
      label: PICK_USED_THIS_WINDOW_LABEL,
    };
  }
  const lockedOutReuse = input.lockedOutTeams.find(
    (team) =>
      team.teamId === input.teamId && team.reason === "reuseLimitReached",
  );
  if (lockedOutReuse) {
    return {
      kind: "lockedOut",
      reason: "reuseLimitReached",
      label: PICK_REUSE_LIMIT_LABEL,
    };
  }
  if (input.selectableTeamIds.includes(input.teamId)) {
    return { kind: "selectable" };
  }
  return {
    kind: "lockedOut",
    reason: "reuseLimitReached",
    label: PICK_REUSE_LIMIT_LABEL,
  };
}

export type typeTeamNameLookup = {
  teamId: string;
  name: string;
  abbreviation: string;
};

function resolveTeamNameAndAbbreviation(
  teamId: string,
  teamsById: ReadonlyMap<string, typeTeamNameLookup>,
): { teamName: string; teamAbbreviation: string } {
  const team = teamsById.get(teamId);
  if (!team) return { teamName: teamId, teamAbbreviation: teamId };
  return { teamName: team.name, teamAbbreviation: team.abbreviation };
}

export function buildPickTiles(input: {
  board: typeBoardData | null;
  teamsById: ReadonlyMap<string, typeTeamNameLookup>;
  selections: readonly typeSelectedSide[];
  isSpreadContest: boolean;
}): typePickTile[] {
  const board = input.board;
  if (board === null) return [];

  return board.games.map((game) => {
    const homeNames = resolveTeamNameAndAbbreviation(
      game.homeTeamId,
      input.teamsById,
    );
    const awayNames = resolveTeamNameAndAbbreviation(
      game.awayTeamId,
      input.teamsById,
    );

    const homeSpreadLabel = input.isSpreadContest
      ? formatSpreadLabel({
          homeLine: game.spread?.locked ?? game.spread?.current,
          isHome: true,
        })
      : null;
    const awaySpreadLabel = input.isSpreadContest
      ? formatSpreadLabel({
          homeLine: game.spread?.locked ?? game.spread?.current,
          isHome: false,
        })
      : null;
    const isSpreadPending = game.spread?.pending === true;
    const unavailableReason = game.unavailable?.reason ?? null;

    return {
      eventId: game.eventId,
      kickoffAt: game.kickoffAt,
      locked: game.locked,
      unavailableReason,
      liveScore: game.liveScore ?? null,
      home: {
        teamId: game.homeTeamId,
        teamName: homeNames.teamName,
        teamAbbreviation: homeNames.teamAbbreviation,
        spreadLabel: homeSpreadLabel,
        isSpreadPending,
        availability: resolveSideAvailability({
          eventId: game.eventId,
          teamId: game.homeTeamId,
          gameLocked: game.locked,
          unavailableReason,
          selections: input.selections,
          selectableTeamIds: board.selectableTeamIds,
          lockedOutTeams: board.lockedOutTeams,
        }),
      },
      away: {
        teamId: game.awayTeamId,
        teamName: awayNames.teamName,
        teamAbbreviation: awayNames.teamAbbreviation,
        spreadLabel: awaySpreadLabel,
        isSpreadPending,
        availability: resolveSideAvailability({
          eventId: game.eventId,
          teamId: game.awayTeamId,
          gameLocked: game.locked,
          unavailableReason,
          selections: input.selections,
          selectableTeamIds: board.selectableTeamIds,
          lockedOutTeams: board.lockedOutTeams,
        }),
      },
    };
  });
}

// ISSUE-777. A contest's startDate is a calendar day, "2027-01-01", not an
// instant. Rendering it through the viewer's own zone would move it back a day
// for everyone west of Greenwich, which is the day edge money-and-time.mdc
// warns about. So the day is pinned to UTC on both sides: the string is read
// as UTC midnight and formatted in UTC, and the digits never move. Mirrors
// formatChangeWindowDate in features/editProfile/editProfileLogic.ts.
export function formatContestStartLabel(startDate: string): string {
  const date = new Date(`${startDate}T00:00:00.000Z`);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
}

export function resolvePickBoardPhase(input: {
  contestStatus: typeContestStatus | null;
  currentRoundId: string | null | undefined;
  entryStatus: typeEntryStatus | null;
  hasEntry: boolean;
  board: typeBoardData | null;
  viewerContestState: typeViewerContestState;
  waitingNote: string | null;
}): typePickBoardPhase {
  // ISSUE-777, first branch on purpose. A waiting fan holds an entry in a
  // contest that has not started, so there is no round and no board to fall
  // through to. Every branch below would land them on a note about a contest
  // that is not theirs.
  if (input.viewerContestState === "waiting" && input.waitingNote !== null) {
    return { kind: "waiting", note: input.waitingNote };
  }
  if (
    input.contestStatus === null ||
    !PICK_PLAYABLE_CONTEST_STATUSES.includes(input.contestStatus) ||
    input.currentRoundId === null ||
    typeof input.currentRoundId === "undefined" ||
    input.currentRoundId === ""
  ) {
    return { kind: "noContest", note: PICK_NO_CONTEST_NOTE };
  }
  if (input.entryStatus === "eliminated") {
    return { kind: "eliminated", note: PICK_ELIMINATED_NOTE };
  }
  if (input.board === null) {
    // ISSUE-764 R4: the "no round" note is unreachable from here, because
    // reaching this line means currentRoundId is a non-empty string and a
    // round therefore exists.
    // ISSUE-777/D4. A fan with no entry used to be told only that they have
    // none. The second line answers the question they actually have, which is
    // what happens next, without promising a contest nobody has started.
    return input.hasEntry
      ? { kind: "boardUnavailable", note: PICK_FAILED_MESSAGE }
      : {
          kind: "noEntry",
          note: `${PICK_NO_ENTRY_NOTE} ${PICK_NEXT_CONTEST_NOTE}`,
        };
  }
  if (input.board.skipDeclared) {
    return { kind: "skipped", note: PICK_SKIPPED_NOTE };
  }
  if (input.board.locked) {
    return { kind: "roundLocked", note: PICK_ROUND_LOCKED_NOTE };
  }
  if (
    input.board.myPicks.length > 0 &&
    input.board.myPicks.some((pick) => pick.locked)
  ) {
    return { kind: "confirmedReadOnly", note: PICK_ROUND_LOCKED_NOTE };
  }
  return {
    kind: "open",
    requiredPickCount: input.board.requiredPickCount,
    canEdit: input.board.myPicks.length > 0,
  };
}

export function isSubmitEnabled(input: {
  phase: typePickBoardPhase;
  selectedCount: number;
}): boolean {
  return (
    input.phase.kind === "open" &&
    input.selectedCount === input.phase.requiredPickCount
  );
}

export function resolvePrimaryLabel(input: {
  requiredPickCount: number;
  canEdit: boolean;
}): string {
  const isMany = input.requiredPickCount > 1;
  if (input.canEdit) {
    return isMany ? PICK_UPDATE_MANY_LABEL : PICK_UPDATE_ONE_LABEL;
  }
  return isMany ? PICK_CONFIRM_MANY_LABEL : PICK_CONFIRM_ONE_LABEL;
}

export function isSelectionDirty(input: {
  selections: readonly typeSelectedSide[];
  myPicks: readonly typeSelectedSide[];
}): boolean {
  const toKey = (side: typeSelectedSide) => `${side.eventId}|${side.teamId}`;
  const selectionKeys = input.selections.map(toKey).sort();
  const myPickKeys = input.myPicks.map(toKey).sort();
  return JSON.stringify(selectionKeys) !== JSON.stringify(myPickKeys);
}
